import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Send, Copy, Sparkles, CheckCircle2, XCircle, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { authFetch } from "@/lib/auth";
import { messageChatApi } from "@/lib/api";
import type { MessageRequest } from "@/types/api";
import type { ProfileTabData } from "./ProfileTab";
import { ResearchPanel, type ResearchInfo, type ResearchSource } from "./ResearchPanel";

export interface ChatMessage {
  role: "assistant" | "user";
  content: string;
}

export interface ChatState {
  threadId: string | null;
  history: ChatMessage[];
  status: "idle" | "pending" | "success" | "error";
  error?: string;
  packageId?: string;
  research?: ResearchInfo;
}

export interface ChatTurnResult {
  role: RoleKey;
  messageType: MessageRequest["message_type"];
  generated: string;
  section1?: string;
  section2?: string;
  section3?: string;
  threadId: string | null;
  packageId?: string;
  research?: ResearchInfo;
  requestPayload: Record<string, unknown>;
  responseData: unknown;
  requestTimestamp: string;
  responseTimestamp: string;
  isFirstTurn: boolean;
}

interface ChatGeneratorProps {
  tabData: ProfileTabData;
  buildParams: (messageType: MessageRequest["message_type"], previousMessages?: string[]) => Record<string, unknown> | null;
  onTurnComplete?: (result: ChatTurnResult) => void;
  initialChatState?: Record<RoleKey, ChatState>;
  onChatStateChange?: (state: Record<RoleKey, ChatState>) => void;
}

export type RoleKey = "main" | "followup_1" | "followup_2" | "closing";

const ROLE_TO_TYPE: Record<RoleKey, MessageRequest["message_type"]> = {
  main: "main",
  followup_1: "followup",
  followup_2: "followup",
  closing: "end-conversation",
};

const emptyState: ChatState = { threadId: null, history: [], status: "idle" };


function parseResearch(
  data: any,
  requested: boolean,
  country?: string,
  errored = false
): ResearchInfo | undefined {
  if (!requested) return undefined;
  if (errored) {
    return { requested: true, country, status: "error", raw: data };
  }

  // Backend ChatResponse exposes `research_context` (already-processed research text)
  // and `context_used` (company context fed to the LLM). Prefer `research_context`
  // when present; fall back to legacy fields for older API versions.
  const contextUsed: string | undefined = Array.isArray(data?.context_used)
    ? data.context_used.filter((s: any) => typeof s === "string" && s.trim()).join("\n\n") || undefined
    : (typeof data?.context_used === "string" && data.context_used.trim()) || undefined;

  const researchContext: string | undefined =
    (typeof data?.research_context === "string" && data.research_context.trim()) || undefined;

  const summary: string | undefined =
    researchContext ||
    (typeof data?.research_summary === "string" && data.research_summary) ||
    (typeof data?.research?.summary === "string" && data.research.summary) ||
    contextUsed ||
    undefined;

  const rawSources =
    data?.research?.sources ||
    data?.sources ||
    data?.research_sources ||
    [];

  const sources: ResearchSource[] = Array.isArray(rawSources)
    ? rawSources
        .map((s: any) => {
          if (typeof s === "string") return { url: s };
          if (s && typeof s === "object" && (s.url || s.link)) {
            return {
              url: s.url || s.link,
              title: s.title || s.name,
              snippet: s.snippet || s.description,
            };
          }
          return null;
        })
        .filter(Boolean) as ResearchSource[]
    : [];

  const hasAny = Boolean((summary && summary.trim().length > 0) || sources.length > 0);
  if (!hasAny) {
    try {
      // eslint-disable-next-line no-console
      console.warn(
        "[ChatGenerator] research requested but no recognized fields. Response keys:",
        data && typeof data === "object" ? Object.keys(data) : typeof data
      );
    } catch {}
  }
  return {
    requested: true,
    country,
    status: hasAny ? "ok" : "empty",
    summary: summary || undefined,
    sources: sources.length > 0 ? sources : undefined,
    raw: data?.research ?? data?.research_summary ?? data?.sources ?? undefined,
  };
}


export function ChatGenerator({ tabData, buildParams, onTurnComplete, initialChatState, onChatStateChange }: ChatGeneratorProps) {
  const { t } = useTranslation("generator");
  const { t: tc } = useTranslation("common");

  // Seed research panel from scrape-time research (if available)
  const seededResearch: ResearchInfo | undefined = (() => {
    const r = tabData.scrapedData?.research;
    if (!r) return undefined;
    if (r.error && !r.context) {
      return { requested: true, country: r.country, status: "error", raw: r.error };
    }
    if (!r.context) return undefined;
    return { requested: true, country: r.country, status: "ok", summary: r.context, raw: r.raw };
  })();

  const createInitialTabs = (): Record<RoleKey, ChatState> => initialChatState ?? {
    main: { ...emptyState, research: seededResearch },
    followup_1: { ...emptyState, research: seededResearch },
    followup_2: { ...emptyState, research: seededResearch },
    closing: { ...emptyState, research: seededResearch },
  };

  // One chat per sub-tab (role). Restore from parent (persisted in ProfileTabsContext) if present.
  // Keep a ref in sync so async API responses can persist state even after tab switches/unmounts.
  const tabsRef = useRef<Record<RoleKey, ChatState>>(createInitialTabs());
  const [tabs, setTabs] = useState<Record<RoleKey, ChatState>>(() => tabsRef.current);
  const [activeRole, setActiveRole] = useState<RoleKey>("main");
  const [userInput, setUserInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Persist chat state upward so it survives tab switches (ProfileTab unmounts on switch).
  const onChatStateChangeRef = useRef(onChatStateChange);
  useEffect(() => { onChatStateChangeRef.current = onChatStateChange; }, [onChatStateChange]);

  const updateTabs = useCallback((updater: (prev: Record<RoleKey, ChatState>) => Record<RoleKey, ChatState>) => {
    const next = updater(tabsRef.current);
    tabsRef.current = next;
    setTabs(next);
    onChatStateChangeRef.current?.(next);
    return next;
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [tabs, activeRole]);


  // Listen for global "mass generate package" event (Cmd/Ctrl+Shift+Enter)
  useEffect(() => {
    const handler = () => {
      // Only trigger if no generation is currently running
      const anyPending = Object.values(tabs).some((s) => s.status === "pending");
      if (!anyPending) handleFullPackage();
    };
    window.addEventListener("sh:mass-generate-package", handler);
    return () => window.removeEventListener("sh:mass-generate-package", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);

  const setRoleState = useCallback((role: RoleKey, updater: (prev: ChatState) => ChatState) => {
    updateTabs((prev) => ({ ...prev, [role]: updater(prev[role]) }));
  }, [updateTabs]);

  const callChat = useCallback(
    async (
      role: RoleKey,
      args: {
        firstTurn: boolean;
        userMessage?: string;
        previousMessages?: string[];
        packageId?: string;
      }
    ): Promise<string | null> => {
      const messageType = ROLE_TO_TYPE[role];
      const state = tabsRef.current[role];

      let body: Record<string, unknown> = {
        message: args.userMessage || "",
        thread_id: state.threadId,
        meta: {
          package_id: args.packageId,
          message_role: role,
          profile_url: tabData.profileUrl,
          profile_name: tabData.profileName,
          project_name: tabData.config.project_name,
          project_id: tabData.config.project_id,
          sender: tabData.config.sender,
        },
      };

      // Capture research request flags from params (first turn) or carry from prev state.
      let researchRequested = state.research?.requested ?? false;
      let researchCountry = state.research?.country;
      if (args.firstTurn) {
        const params = buildParams(messageType, args.previousMessages);
        if (!params) return null;
        body.params = params;
        researchRequested = Boolean((params as any).research_mode);
        researchCountry = (params as any).research_country || undefined;
      }

      const requestTimestamp = new Date().toISOString();

      setRoleState(role, (p) => ({
        ...p,
        status: "pending",
        history: args.userMessage
          ? [...p.history, { role: "user", content: args.userMessage }]
          : p.history,
      }));

      try {
        const res = await authFetch(messageChatApi(), {
          method: "POST",
          body: JSON.stringify(body),
        });
        const data = await res.json();
        const responseTimestamp = new Date().toISOString();
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

        const generated: string = data.generated_message || "";
        const upstreamHistory: ChatMessage[] = Array.isArray(data.chat_history)
          ? data.chat_history
          : [];

        const research = parseResearch(data, researchRequested, researchCountry);

        // Toast feedback for research (only on first turn that requested it).
        if (args.firstTurn && research) {
          if (research.status === "ok") {
            toast({
              title: tc("success", { defaultValue: "Sukces" }),
              description: `Research firmy: pobrano${
                research.sources?.length ? ` ${research.sources.length} źródeł` : " dane"
              }`,
            });
          } else if (research.status === "empty") {
            toast({
              title: "Research firmy",
              description: `Brak danych researchu${research.country ? ` dla ${research.country}` : ""}.`,
            });
          }
        }

        const latestState = tabsRef.current[role];
        const newThreadId = data.thread_id || latestState.threadId || state.threadId || null;
        const effectiveResearch = research ?? latestState.research ?? state.research;

        setRoleState(role, (p) => ({
          ...p,
          status: "success",
          threadId: newThreadId,
          packageId: args.packageId || p.packageId,
          research: effectiveResearch,
          history:
            upstreamHistory.length > 0
              ? upstreamHistory
              : generated
                ? [...p.history, { role: "assistant", content: generated }]
                : p.history,
        }));

        // Notify parent (ProfileTab) so it can update GeneratedMessage card,
        // dev mode panel, and trigger TTS-cleanup auto-fetch.
        if (onTurnComplete && generated) {
          onTurnComplete({
            role,
            messageType,
            generated,
            section1: typeof data.section1 === "string" ? data.section1 : undefined,
            section2: typeof data.section2 === "string" ? data.section2 : undefined,
            section3: typeof data.section3 === "string" ? data.section3 : undefined,
            threadId: newThreadId,
            packageId: args.packageId || state.packageId,
            research: effectiveResearch,
            requestPayload: body,
            responseData: data,
            requestTimestamp,
            responseTimestamp,
            isFirstTurn: args.firstTurn,
          });
        }

        return generated;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setRoleState(role, (p) => ({
          ...p,
          status: "error",
          error: message,
          research: researchRequested
            ? { requested: true, country: researchCountry, status: "error" }
            : p.research,
        }));
        toast({ title: tc("error"), description: message, variant: "destructive" });
        return null;
      }
    },
    [tabData, buildParams, setRoleState, tc, onTurnComplete]
  );


  const handleSingleGenerate = async () => {
    await callChat(activeRole, { firstTurn: true });
  };

  const handleUserPrompt = async () => {
    const prompt = userInput.trim();
    if (!prompt) return;
    setUserInput("");
    const state = tabs[activeRole];
    // If no thread yet, treat first user message as initial turn with the prompt
    await callChat(activeRole, { firstTurn: !state.threadId, userMessage: prompt });
  };

  const handleFullPackage = async () => {
    // Pre-flight validation — buildParams toasts a clear error and returns null
    // if project/sender/other required config is missing. Bail BEFORE flipping
    // any tab to "pending" so the user doesn't see generation start.
    const preflight = buildParams("main");
    if (!preflight) return;

    const packageId = crypto.randomUUID();
    // Reset all four tabs
    updateTabs(() => ({
      main: { ...emptyState, packageId, status: "pending" },
      followup_1: { ...emptyState, packageId, status: "pending" },
      followup_2: { ...emptyState, packageId, status: "pending" },
      closing: { ...emptyState, packageId, status: "pending" },
    }));
    setActiveRole("main");


    // main and followup_1 in parallel
    const [main, fu1] = await Promise.all([
      callChat("main", { firstTurn: true, packageId }),
      callChat("followup_1", { firstTurn: true, packageId, previousMessages: [] }),
    ]);

    if (main) {
      const fu2 = await callChat("followup_2", {
        firstTurn: true,
        packageId,
        previousMessages: [main, fu1 || ""].filter(Boolean),
      });
      await callChat("closing", {
        firstTurn: true,
        packageId,
        previousMessages: [main, fu1 || "", fu2 || ""].filter(Boolean),
      });
    }
  };

  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
    toast({ title: tc("success"), description: t("toast.copiedToClipboard") });
  };

  const renderStatusBadge = (role: RoleKey) => {
    const s = tabs[role].status;
    if (s === "pending") return <Loader2 className="h-3 w-3 animate-spin" />;
    if (s === "success") return <CheckCircle2 className="h-3 w-3 text-green-600" />;
    if (s === "error") return <XCircle className="h-3 w-3 text-destructive" />;
    return null;
  };

  const currentState = tabs[activeRole];
  const isPending = currentState.status === "pending";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Badge variant="outline" className="gap-1">
          <Sparkles className="h-3 w-3" />
          {t("chat.betaBadge", { defaultValue: "Tryb chat (beta)" })}
        </Badge>
        <Button onClick={handleFullPackage} disabled={isPending} size="sm" variant="secondary">
          <Package className="h-4 w-4 mr-2" />
          {t("chat.fullPackage", { defaultValue: "Wygeneruj pakiet (4)" })}
        </Button>
      </div>

      <Tabs value={activeRole} onValueChange={(v) => setActiveRole(v as RoleKey)}>
        <TabsList className="grid w-full grid-cols-4">
          {(Object.keys(ROLE_TO_TYPE) as RoleKey[]).map((role) => (
            <TabsTrigger key={role} value={role} className="gap-1.5">
              <span className="truncate">
                {t(`chat.role.${role}`, {
                  defaultValue:
                    role === "main"
                      ? "Główna"
                      : role === "followup_1"
                      ? "FU 1"
                      : role === "followup_2"
                      ? "FU 2"
                      : "Kończąca",
                })}
              </span>
              {renderStatusBadge(role)}
            </TabsTrigger>
          ))}
        </TabsList>

        {(Object.keys(ROLE_TO_TYPE) as RoleKey[]).map((role) => (
          <TabsContent key={role} value={role} className="mt-3 space-y-2">
            {tabs[role].research && (
              <ResearchPanel info={tabs[role].research!} />
            )}
            <div className="border rounded-md bg-card">
              <ScrollArea className="h-[420px] p-4" ref={scrollRef as any}>
                {tabs[role].history.length === 0 && tabs[role].status === "idle" && (
                  <div className="text-sm text-muted-foreground text-center py-12">
                    {t("chat.empty", {
                      defaultValue:
                        'Naciśnij „Generuj" aby utworzyć pierwszą wersję wiadomości, a następnie kontynuuj rozmowę z AI.',
                    })}
                  </div>
                )}
                {tabs[role].history.map((m, i) => (
                  <div
                    key={i}
                    className={cn(
                      "mb-3 flex",
                      m.role === "user" ? "justify-end" : "justify-start"
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap",
                        m.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "text-foreground"
                      )}
                    >
                      {m.content}
                      {m.role === "assistant" && (
                        <div className="mt-2 flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2"
                            onClick={() => handleCopy(m.content)}
                          >
                            <Copy className="h-3 w-3 mr-1" /> {tc("copy", { defaultValue: "Kopiuj" })}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {tabs[role].status === "pending" && (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("chat.generating", { defaultValue: "Generuję…" })}
                  </div>
                )}
                {tabs[role].status === "error" && tabs[role].error && (
                  <div className="text-sm text-destructive">{tabs[role].error}</div>
                )}
              </ScrollArea>

              <div className="border-t p-3 space-y-2">
                {tabs[role].history.length === 0 && (
                  <Button
                    onClick={handleSingleGenerate}
                    disabled={isPending}
                    className="w-full"
                  >
                    {isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    {t("chat.generate", { defaultValue: "Generuj" })}
                  </Button>
                )}
                {tabs[role].history.length > 0 && (
                  <div className="flex gap-2">
                    <Textarea
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      placeholder={t("chat.placeholder", {
                        defaultValue: 'Np. "Skróć", "Bardziej formalnie", "Dodaj CTA"…',
                      })}
                      rows={2}
                      className="flex-1 resize-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                          e.preventDefault();
                          if (!isPending && userInput.trim()) handleUserPrompt();
                        }
                      }}
                    />
                    <Button onClick={handleUserPrompt} disabled={isPending || !userInput.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
